/**
 * Public types for @alchemiesolns/user-access. All types here are
 * package-owned — they do not alias or re-export Firebase types.
 */

export interface FirebaseWebConfig {
  apiKey: string | undefined;
  authDomain?: string | undefined;
  projectId: string | undefined;
  storageBucket?: string | undefined;
  messagingSenderId?: string | undefined;
  appId?: string | undefined;
}

/**
 * Package behaviour, separate from the Firebase web app config above.
 */
export interface UserAccessOptions {
  /**
   * Absolute URL an email action link returns the user to once Firebase has
   * completed the action — verification and password reset alike. Omit to use
   * the Firebase project default.
   *
   * **The domain must be listed in Firebase Auth's authorized domains**, or the
   * send throws `invalid-continue-url`. That throw aborts registration in
   * `registerFirebaseAccount`, so a wrong value here is a signup outage, not a
   * cosmetic bug. It fails on the first attempt in any environment, which is
   * why this is explicit config rather than derived from `location.origin`.
   */
  continueUrl?: string;
}

export interface AuthedUser {
  uid: string;
  email: string | null;
  emailVerified: boolean;
  displayName: string | null;
  photoURL: string | null;
  idToken: string;
  /**
   * True when this sign-in created (or is the first sign-in for) the account.
   * For email/password signup this is always true; for login it's always false.
   * For OAuth sign-in the value is populated from Firebase's
   * `additionalUserInfo.isNewUser` — true only on the first sign-in with that provider.
   *
   * Consumers use this to decide whether to mint a profile via
   * `@alchemiesolns/store-api`'s `saveUserProfile`.
   */
  isNew: boolean;
}

export interface CurrentUser {
  uid: string;
  email: string | null;
  emailVerified: boolean;
  displayName: string | null;
  photoURL: string | null;
}

export type OAuthProvider = 'google';

export type UserAccessErrorCode =
  | 'wrong-password'
  | 'user-not-found'
  | 'email-in-use'
  | 'invalid-email'
  | 'weak-password'
  | 'operation-not-allowed'
  | 'too-many-requests'
  | 'user-disabled'
  | 'popup-blocked'
  | 'popup-closed'
  | 'network'
  | 'not-initialized'
  | 'invalid-continue-url'
  | 'unknown';

export class UserAccessError extends Error {
  readonly code: UserAccessErrorCode;
  constructor(code: UserAccessErrorCode, message: string) {
    super(message);
    this.name = 'UserAccessError';
    this.code = code;
  }
}
